import React from 'react';
import styled from '@emotion/styled';
import MainViewHeader from '../../components/MainViewHeader';
import { ViewModeSelector, ViewModeButton } from '../../components/ViewModeSelector';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import ScenarioPicker from './ScenarioPicker';
import ScenarioIcon from './ScenarioIcon';

const ScenarioLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 10px 0 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  white-space: nowrap;
`;

/**
 * ImpactPathHeader - Wraps MainViewHeader with Impact Path-specific controls
 */
export default function ImpactPathHeader({
  // v1's participant flow chooses a scenario before drawing; the v2 map shows
  // every scenario at once and hides the switch.
  showScenarioSwitch = true,
  // Scenario picker in place of the plain label: the list and a setter.
  scenarios = null,
  onSelectScenario = null,
  currentViewMode = 'individual',
  scenarioId = '',
  scenarioInfo = null,
  scenarioColor = null,
  instanceTitle = '',
  onViewModeChange = () => {},
  // A participant who came in from /me sees their own view only.
  lockView = false,
  onChangeScenario = () => {},
  onSettingsClick = () => {},
  isStorybook = false,
  disableNavigation = false,
  onViewChange = null,
  inDocumentFlow = false,
  // Rendered next to the instance title — used by the v2 map for its preview
  // badge, the same slot the perspective badge uses elsewhere.
  subtitleExtra = null,
}) {
  const { t } = useTranslation();
  const onMap = (currentViewMode === 'individual' || currentViewMode === 'scenario') && !!scenarioId;

  return (
    <MainViewHeader
      activeView="impact_path"
      instanceTitle={instanceTitle}
      subtitleExtra={subtitleExtra}
      isStorybook={isStorybook}
      inDocumentFlow={inDocumentFlow}
      onSettingsClick={onSettingsClick}
      disableNavigation={disableNavigation}
      onViewChange={onViewChange}
    >
      {showScenarioSwitch && (
      <ViewModeSelector>
        <ViewModeButton
          $active={!onMap}
          onClick={onChangeScenario}
        >
          {t('navigation.impactPathScenarios')}
        </ViewModeButton>
        <ViewModeButton
          $active={onMap}
          onClick={() => {
            // Already on the map when a scenario is selected; Skenaariot clears it.
            if (!scenarioId) onChangeScenario();
          }}
        >
          {t('navigation.impactPathMap')}
        </ViewModeButton>
      </ViewModeSelector>
      )}

      {onMap && scenarioInfo?.title && (Array.isArray(scenarios) && scenarios.length > 0 && onSelectScenario ? (
        <ScenarioPicker scenarios={scenarios} value={scenarioId} onChange={onSelectScenario} />
      ) : (
        <ScenarioLabel>
          <ScenarioIcon title={scenarioInfo.title} icon={scenarioInfo.icon} />
          {scenarioInfo.title}
        </ScenarioLabel>
      ))}

      {!lockView && (
      <ViewModeSelector>
        <ViewModeButton
          $active={currentViewMode === 'individual'}
          onClick={() => onViewModeChange('individual')}
        >
          {t('impactPath.viewMode.mineLabel')}
        </ViewModeButton>
        <ViewModeButton
          $active={currentViewMode === 'scenario'}
          onClick={() => onViewModeChange('scenario')}
        >
          {t('impactPath.viewMode.scenarioLabel')}
        </ViewModeButton>
        <ViewModeButton
          $active={currentViewMode === 'instance'}
          onClick={() => onViewModeChange('instance')}
        >
          {t('impactPath.viewMode.allLabel')}
        </ViewModeButton>
      </ViewModeSelector>
      )}
    </MainViewHeader>
  );
}
